import { ChevronsUpDown, Zap } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router";
import { HazardStripe } from "@/components/gb/hazard-stripe";
import { useComplianceAlerts } from "@/hooks/use-site";
import { cn } from "@/lib/utils";
import { NAV_ITEMS, navIcon, navItemForPath } from "./nav";
import { useProjectContext } from "./project-context";

/**
 * The wordmark block at the top of the rail.
 *
 * Extracted so the picker's chrome — which has a rail but no nav — can reuse it
 * rather than duplicating the markup or teaching `Sidebar` a second mode.
 * Linking it to the picker gives every project screen a way back out.
 */
export function SidebarBrand() {
  return (
    <Link
      to="/"
      className="flex items-center gap-[11px] px-[22px] pt-[22px] pb-[18px] transition-opacity hover:opacity-85"
    >
      <div className="flex size-[38px] shrink-0 items-center justify-center rounded-tile bg-linear-to-br from-brand to-brand-hover text-white">
        <Zap size={20} strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-[1.28rem] leading-none font-bold tracking-[0.02em] text-white uppercase">
          GroundBrain
        </div>
        <div className="mt-[3px] text-[0.66rem] font-semibold tracking-[0.14em] text-brand-bright uppercase">
          Office Console
        </div>
      </div>
    </Link>
  );
}

/**
 * Which project you are looking at, and the way out of it.
 *
 * The nav below is identical on every project — Today, Crew, Journal, Brain,
 * Project — so without this the rail gives no clue which site those five items
 * belong to. The topbar eyebrow names it, but that is one line at the far side
 * of the screen and it scrolls out of mind; the thing you navigate with should
 * say what you are navigating.
 *
 * It doubles as the switcher. Returning to the picker was previously only
 * possible by clicking the wordmark, which nothing advertises.
 */
function CurrentProject() {
  const { project } = useProjectContext();

  return (
    <div className="px-[14px] pb-[14px]">
      <Link
        to="/"
        className="group block rounded-input border border-white/10 bg-white/5 px-[13px] py-[10px] transition-colors hover:border-brand/50 hover:bg-white/8"
        // The name alone reads as a heading rather than a control, so the
        // accessible name has to carry the action.
        aria-label={`Current project: ${project.name}. Switch project`}
      >
        <div className="flex items-center gap-1.5">
          <span className="font-display text-[0.6rem] font-semibold tracking-[0.14em] text-white/40 uppercase">
            Project
          </span>
          {project.status !== "active" && (
            <span
              className={cn(
                "rounded-full px-1.5 font-display text-[0.55rem] font-semibold tracking-[0.08em] uppercase",
                project.status === "archived"
                  ? "bg-white/10 text-white/50"
                  : "bg-warn/20 text-warn",
              )}
            >
              {project.status === "archived" ? "Archived" : "In setup"}
            </span>
          )}
        </div>
        <div className="mt-[3px] flex items-center gap-1.5">
          {/* `truncate` needs a min-width-0 flex child or a long site name
              pushes the chevron out of the rail instead of ellipsing. */}
          <span className="min-w-0 flex-1 truncate font-display text-[0.95rem] font-semibold tracking-[0.01em] text-white">
            {project.name}
          </span>
          <ChevronsUpDown
            size={14}
            className="shrink-0 text-white/35 transition-colors group-hover:text-brand-bright"
          />
        </div>
      </Link>
    </div>
  );
}

/** The 256px dark rail, without nav. Used by the picker. */
export function SidebarRail({ children }: { children?: React.ReactNode }) {
  return (
    <aside className="relative flex w-64 shrink-0 flex-col border-r border-black bg-chrome">
      <HazardStripe height={4} />
      <SidebarBrand />
      {children}
    </aside>
  );
}

/**
 * The 256px dark rail. Fixed width, never collapses — the design has no
 * collapsed state and the content column absorbs all the flex.
 *
 * Every `to` here is **relative**, and resolves against the route that renders
 * this component — `ProjectLayout`, mounted at `projects/:projectId`. So
 * `to="crew"` is `/projects/p1/crew` even when the user is three segments deep
 * at `/projects/p1/setup/2`. If this component is ever moved to a route nested
 * *below* that one, the links start resolving relative to the deeper path and
 * every one of them 404s into the picker — which reads as "clicking Crew signs
 * me out". Keep it directly under `ProjectLayout`.
 */
export function Sidebar() {
  const alerts = useComplianceAlerts();
  const { pathname } = useLocation();
  const { projectId } = useProjectContext();

  // Active state is computed with the same matcher the topbar uses, rather than
  // left to `NavLink`'s own `isActive`. The two disagree wherever a section
  // owns more than one first segment: at `/setup/2` the topbar correctly says
  // "Project" (via `matches`), but `NavLink to="overview"` does not match, so
  // the rail would highlight nothing at all. One matcher, one answer.
  const prefix = `/projects/${projectId}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : "";
  const active = navItemForPath(rest);

  return (
    <SidebarRail>
      <CurrentProject />
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-[14px] py-1">
        {NAV_ITEMS.map(({ to, label, showsAlertBadge }) => {
          const Icon = navIcon(to);
          const isActive = active?.to === to;
          return (
            <div key={to}>
            <NavLink
              to={to}
              className={cn(
                "flex w-full items-center gap-3 rounded-input px-[13px] py-[11px] transition-colors",
                isActive
                  ? "bg-brand-hover/14 text-brand-glow"
                  : "text-white/62 hover:bg-white/5",
              )}
            >
              <span
                className={cn("flex shrink-0", isActive ? "text-brand-glow" : "text-white/55")}
              >
                <Icon size={21} strokeWidth={isActive ? 2 : 1.7} />
              </span>
              <span className="flex-1 text-left font-display text-[0.92rem] font-medium tracking-[0.04em] uppercase">
                {label}
              </span>
              {/*
                The count comes from `useComplianceAlerts()`, which is still
                fixture-backed — so it is the SAME on every project. That is a
                known gap, not a bug to fix by inventing per-project data: crew
                has no endpoint yet. It goes away when the roster does.
              */}
              {showsAlertBadge && alerts.length > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-[5px] font-display text-[0.68rem] font-semibold text-white">
                  {alerts.length}
                </span>
              )}
            </NavLink>
            {/* Nothing hangs off Brain any more. It used to carry an "Ask a
                question" link; Brain now *lands* on Ask, and the tab bar on the
                screen offers it — so the rail entry was a second way in a few
                pixels from the first, which is the thing this file keeps
                refusing to do elsewhere. */}
            </div>
          );
        })}
      </nav>
    </SidebarRail>
  );
}
